import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { stockBadge, stockPct, stockBarColor, Modal, AdjustModal, useForm, today } from '../components/helpers';

const EMPTY = { name: '', description: '', on_hand: '', reorder_at: '' };

export default function BulkParts() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [editing, setEditing] = useState(null);
  const [adjusting, setAdjusting] = useState(null);
  const [form, set, setForm] = useForm(EMPTY);

  async function load() {
    const { data } = await supabase.from('bulk_parts').select('*').order('name');
    setItems(data || []);
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  function openAdd() { setForm(EMPTY); setEditing(null); setModal('form'); }
  function openEdit(item) {
    setForm({ name: item.name, description: item.description || '', on_hand: item.on_hand, reorder_at: item.reorder_at });
    setEditing(item); setModal('form');
  }

  async function save() {
    if (!form.name || form.on_hand === '' || form.reorder_at === '') { alert('Name, on hand, and reorder are required.'); return; }
    const payload = { name: form.name.trim(), description: form.description.trim(), on_hand: Number(form.on_hand), reorder_at: Number(form.reorder_at), updated_at: today() };
    if (editing) {
      await supabase.from('bulk_parts').update(payload).eq('id', editing.id);
    } else {
      await supabase.from('bulk_parts').insert(payload);
    }
    setModal(null); load();
  }

  async function applyAdjust(delta, reason) {
    const newVal = Math.max(0, Number(adjusting.on_hand) + delta);
    await supabase.from('bulk_parts').update({ on_hand: newVal, updated_at: today() }).eq('id', adjusting.id);
    await supabase.from('inventory_adjustments').insert({ item_type: 'raw', item_id: adjusting.id, item_name: adjusting.name, adjustment: delta, reason });
    setModal(null); load();
  }

  async function del(id) {
    if (!window.confirm('Delete this bulk part?')) return;
    await supabase.from('bulk_parts').delete().eq('id', id);
    load();
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Bulk Parts</div>
          <div className="page-sub">Labeled &amp; foamed parts ready to pack — PBC and RC only</div>
        </div>
        <button className="btn btn-primary" onClick={openAdd}>+ Add Bulk Part</button>
      </div>

      <div style={{ background: 'var(--accent-light)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: 'var(--accent)' }}>
        Production runs for PBC/RC add units here. Pack-out runs pull from here and move units into customer-specific Finished Goods SKUs.
      </div>

      <div className="tbl-wrap">
        <table>
          <thead><tr><th>Bulk Part</th><th>Description</th><th>On Hand (units)</th><th>Reorder At</th><th>Status</th><th>Updated</th><th>Actions</th></tr></thead>
          <tbody>
            {loading && <tr className="empty-row"><td colSpan={7}>Loading...</td></tr>}
            {!loading && items.length === 0 && <tr className="empty-row"><td colSpan={7}>No bulk parts yet. Add PBC and RC to get started.</td></tr>}
            {items.map(item => {
              const pct = stockPct(item.on_hand, item.reorder_at);
              const barColor = stockBarColor(item.on_hand, item.reorder_at);
              return (
                <tr key={item.id}>
                  <td><strong>{item.name}</strong></td>
                  <td className="text-muted" style={{ fontSize: 12 }}>{item.description || '—'}</td>
                  <td className="mono">
                    {Number(item.on_hand).toLocaleString()} units
                    <div className="progress-wrap"><div className="progress-bar" style={{ width: pct + '%', background: barColor }} /></div>
                  </td>
                  <td className="mono">{Number(item.reorder_at).toLocaleString()}</td>
                  <td>{stockBadge(item.on_hand, item.reorder_at)}</td>
                  <td className="text-muted" style={{ fontSize: 12 }}>{item.updated_at || '—'}</td>
                  <td>
                    <div className="flex-row">
                      <button className="btn btn-sm" onClick={() => { setAdjusting(item); setModal('adjust'); }}>+/−</button>
                      <button className="btn btn-sm" onClick={() => openEdit(item)}>Edit</button>
                      <button className="btn btn-sm btn-danger" onClick={() => del(item.id)}>Del</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {modal === 'form' && (
        <Modal title={editing ? 'Edit Bulk Part' : 'Add Bulk Part'} onClose={() => setModal(null)} onSave={save}>
          <div className="form-group"><label className="form-label">Name *</label><input className="form-input" value={form.name} onChange={set('name')} placeholder="e.g. PBC — Brush Cover (Labeled)" autoFocus /></div>
          <div className="form-group"><label className="form-label">Description</label><input className="form-input" value={form.description} onChange={set('description')} placeholder="e.g. Front/back labels and foam applied" /></div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">On Hand (units) *</label><input className="form-input mono" type="number" min="0" value={form.on_hand} onChange={set('on_hand')} placeholder="0" /></div>
            <div className="form-group"><label className="form-label">Reorder At (units) *</label><input className="form-input mono" type="number" min="0" value={form.reorder_at} onChange={set('reorder_at')} placeholder="0" /></div>
          </div>
        </Modal>
      )}
      {modal === 'adjust' && adjusting && (<AdjustModal item={adjusting} unit="units" onClose={() => setModal(null)} onSave={applyAdjust} />)}
    </div>
  );
}
